import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { EyeIcon, EyeOffIcon, LockIcon, ArrowLeftIcon, CheckCircle2Icon, ChevronDownIcon } from 'lucide-react';
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0
  })
};
export function SignupPage() {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [remoteAssist, setRemoteAssist] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'free' | 'individual' | 'family'>('individual');
  const goToStep = (newStep: number) => {
    setDirection(newStep > step ? 1 : -1);
    setStep(newStep);
  };
  const handlePlanSelect = (plan: 'free' | 'individual' | 'family') => {
    setSelectedPlan(plan);
    goToStep(3);
  };
  return <div className="min-h-screen bg-[#f8f8f8] flex flex-col items-center overflow-hidden font-sans" data-id="element-4947">
      <div className="w-full max-w-md mx-auto relative min-h-screen flex flex-col" data-id="element-4948">
        {/* Header for Steps 2 & 3 */}
        <div className="h-16 flex items-center px-5 shrink-0 z-10 relative" data-id="element-4949">
          {step > 1 && <button onClick={() => goToStep(step - 1)} className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#111111] hover:bg-gray-50 transition-colors cursor-pointer" data-id="element-4950">
              <ArrowLeftIcon size={20} data-id="element-4951" />
            </button>}
        </div>

        <div className="flex-1 relative w-full flex flex-col" data-id="element-4952">
          <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-4953">
            {step === 1 && <motion.div key="step1" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} className="absolute inset-0 w-full flex flex-col px-6 pb-10 overflow-y-auto hide-scrollbar" data-id="element-4954">
                <div className="flex flex-col items-center text-center mb-8 mt-4" data-id="element-4955">
                  <img src="https://cdn.magicpatterns.com/uploads/u2RsmpFkExbV8oAVAJrtqh/Caveo-Lockup-FC.png" alt="Caveo" className="h-7 mb-8" data-id="element-4956" />
                  <h1 className="text-3xl font-bold text-[#1a0647] mb-2" data-id="element-4957">
                    Create your free account.
                  </h1>
                  <p className="text-gray-500 text-[15px]" data-id="element-4958">
                    No credit card required to get started.
                  </p>
                </div>

                <div className="space-y-3 mb-6" data-id="element-4959">
                  <button className="w-full bg-white border border-gray-300 text-[#111111] font-semibold py-3.5 rounded-xl flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-4960">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" data-id="element-4961">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" data-id="element-4962" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" data-id="element-4963" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" data-id="element-4964" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" data-id="element-4965" />
                    </svg>
                    Continue with Google
                  </button>
                  <button className="w-full bg-white border border-gray-300 text-[#111111] font-semibold py-3.5 rounded-xl flex items-center justify-center gap-3 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-4966">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-4967">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.04 2.26-.74 3.58-.79 1.54-.04 2.74.69 3.48 1.84-3.02 1.76-2.51 5.62.44 6.85-.71 1.76-1.57 3.28-2.58 4.27zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" data-id="element-4968" />
                    </svg>
                    Continue with Apple
                  </button>
                </div>

                <div className="flex items-center gap-4 mb-6" data-id="element-4969">
                  <div className="flex-1 h-px bg-gray-200" data-id="element-4970"></div>
                  <span className="text-gray-400 text-sm font-medium" data-id="element-4971">or</span>
                  <div className="flex-1 h-px bg-gray-200" data-id="element-4972"></div>
                </div>

                <div className="space-y-4 mb-8" data-id="element-4973">
                  <div data-id="element-4974">
                    <input type="text" placeholder="Full Name" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-[#111111] placeholder-gray-400 focus:outline-none focus:border-[#8528e2] focus:ring-1 focus:ring-[#8528e2] transition-all" data-id="element-4975" />
                  </div>
                  <div data-id="element-4976">
                    <input type="email" placeholder="Email Address" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-[#111111] placeholder-gray-400 focus:outline-none focus:border-[#8528e2] focus:ring-1 focus:ring-[#8528e2] transition-all" data-id="element-4977" />
                  </div>
                  <div className="relative" data-id="element-4978">
                    <input type={showPassword ? 'text' : 'password'} placeholder="Password" className="w-full bg-white border border-gray-200 rounded-xl pl-4 pr-12 py-3.5 text-[#111111] placeholder-gray-400 focus:outline-none focus:border-[#8528e2] focus:ring-1 focus:ring-[#8528e2] transition-all" data-id="element-4979" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-4980">
                      {showPassword ? <EyeOffIcon size={20} data-id="element-4981" /> : <EyeIcon size={20} data-id="element-4982" />}
                    </button>
                  </div>
                </div>

                <button onClick={() => goToStep(2)} className="w-full bg-[#8528e2] text-white font-bold py-4 rounded-xl hover:bg-[#7322c4] transition-colors shadow-md mb-4 text-lg" data-id="element-4983">
                  Create Account
                </button>

                <p className="text-center text-xs text-gray-500 mb-6 px-4" data-id="element-4984">
                  By creating an account, you agree to our{' '}
                  <Link to="/terms" className="underline text-gray-600" data-id="element-4985">
                    Terms
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="underline text-gray-600" data-id="element-4986">
                    Privacy Policy
                  </Link>
                  .
                </p>

                <div className="mt-auto pt-6 flex items-center justify-center gap-2 text-[#0EB4CC] font-medium text-sm" data-id="element-4987">
                  <LockIcon size={16} data-id="element-4988" />
                  <span data-id="element-4989">We will never sell your data. Ever.</span>
                </div>
              </motion.div>}

            {step === 2 && <motion.div key="step2" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} className="absolute inset-0 w-full flex flex-col px-5 pb-10 overflow-y-auto hide-scrollbar" data-id="element-4990">
                <div className="text-center mb-6" data-id="element-4991">
                  <h2 className="text-2xl font-bold text-[#1a0647] mb-2" data-id="element-4992">
                    Choose your plan
                  </h2>
                  <p className="text-gray-500 text-sm" data-id="element-4993">
                    Upgrade anytime. 14-day free trial on paid plans.
                  </p>
                </div>

                {/* Toggle */}
                <div className="flex justify-center mb-8" data-id="element-4994">
                  <div className="bg-gray-200 p-1 rounded-full flex items-center relative" data-id="element-4995">
                    <button onClick={() => setBillingCycle('monthly')} className={`relative z-10 px-5 py-2 text-sm font-semibold rounded-full transition-colors ${billingCycle === 'monthly' ? 'text-[#111111]' : 'text-gray-500'}`} data-id="element-4996">
                      Monthly
                    </button>
                    <button onClick={() => setBillingCycle('annual')} className={`relative z-10 px-5 py-2 text-sm font-semibold rounded-full transition-colors flex items-center gap-2 ${billingCycle === 'annual' ? 'text-[#111111]' : 'text-gray-500'}`} data-id="element-4997">
                      Annual
                      <span className="bg-[#0EB4CC] text-white text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md font-bold" data-id="element-4998">
                        Save 17%
                      </span>
                    </button>
                    <div className="absolute top-1 bottom-1 w-[calc(50%-4px)] bg-white rounded-full shadow-sm transition-transform duration-300 ease-in-out" style={{
                  transform: billingCycle === 'annual' ? 'translateX(100%)' : 'translateX(0)',
                  left: billingCycle === 'annual' ? '8px' : '4px'
                }} data-id="element-4999"></div>
                  </div>
                </div>

                {/* Plans */}
                <div className="space-y-4 mb-6" data-id="element-5000">
                  {/* Free Plan */}
                  <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm" data-id="element-5001">
                    <h3 className="text-lg font-bold text-[#111111] mb-1" data-id="element-5002">
                      Free
                    </h3>
                    <div className="text-2xl font-bold text-[#111111] mb-4" data-id="element-5003">
                      $0
                    </div>
                    <ul className="space-y-2 mb-5" data-id="element-5004">
                      <li className="flex items-start gap-2 text-sm text-gray-600" data-id="element-5005">
                        <CheckCircle2Icon size={16} className="text-gray-400 shrink-0 mt-0.5" data-id="element-5006" />
                        Quick-start guided actions
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-600" data-id="element-5007">
                        <CheckCircle2Icon size={16} className="text-gray-400 shrink-0 mt-0.5" data-id="element-5008" />
                        Core protection setup
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-600" data-id="element-5009">
                        <CheckCircle2Icon size={16} className="text-gray-400 shrink-0 mt-0.5" data-id="element-5010" />
                        Emergency response guides
                      </li>
                    </ul>
                    <button onClick={() => handlePlanSelect('free')} className="w-full py-3 rounded-xl font-bold text-[#111111] bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-5011">
                      Start Free
                    </button>
                  </div>

                  {/* Individual Plan */}
                  <div className="bg-[#f0ebf8] border-2 border-[#8528e2] rounded-2xl p-5 shadow-md relative" data-id="element-5012">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#8528e2] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-5013">
                      Most Popular
                    </div>
                    <h3 className="text-lg font-bold text-[#111111] mb-1" data-id="element-5014">
                      Individual
                    </h3>
                    <div className="flex items-baseline gap-1 mb-4" data-id="element-5015">
                      <span className="text-3xl font-bold text-[#111111]" data-id="element-5016">
                        ${billingCycle === 'annual' ? '7.99' : '9.99'}
                      </span>
                      <span className="text-gray-500 text-sm" data-id="element-5017">/month</span>
                    </div>
                    <ul className="space-y-2 mb-5" data-id="element-5018">
                      <li className="flex items-start gap-2 text-sm text-gray-800" data-id="element-5019">
                        <CheckCircle2Icon size={16} className="text-[#8528e2] shrink-0 mt-0.5" data-id="element-5020" />
                        Full personalized action plan
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-800" data-id="element-5021">
                        <CheckCircle2Icon size={16} className="text-[#8528e2] shrink-0 mt-0.5" data-id="element-5022" />
                        Progress tracking
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-800" data-id="element-5023">
                        <CheckCircle2Icon size={16} className="text-[#8528e2] shrink-0 mt-0.5" data-id="element-5024" />
                        Protection Score
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-800" data-id="element-5025">
                        <CheckCircle2Icon size={16} className="text-[#8528e2] shrink-0 mt-0.5" data-id="element-5026" />
                        Guided action queue
                      </li>
                    </ul>
                    <button onClick={() => handlePlanSelect('individual')} className="w-full py-3 rounded-xl font-bold text-white bg-[#8528e2] hover:bg-[#7322c4] transition-colors shadow-sm" data-id="element-5027">
                      Start Individual Plan
                    </button>
                  </div>

                  {/* Family Plan */}
                  <div className="bg-[#1a0647] border border-[#1a0647] rounded-2xl p-5 shadow-md relative" data-id="element-5028">
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0EB4CC] text-[#1a0647] text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider" data-id="element-5029">
                      Best Value
                    </div>
                    <h3 className="text-lg font-bold text-white mb-1" data-id="element-5030">
                      Family
                    </h3>
                    <div className="flex items-baseline gap-1 mb-1" data-id="element-5031">
                      <span className="text-3xl font-bold text-white" data-id="element-5032">
                        ${billingCycle === 'annual' ? '19.99' : '24.99'}
                      </span>
                      <span className="text-gray-400 text-sm" data-id="element-5033">/month</span>
                    </div>
                    <p className="text-xs text-[#0EB4CC] mb-4 font-medium" data-id="element-5034">
                      Up to 6 members
                    </p>
                    <ul className="space-y-2 mb-5" data-id="element-5035">
                      <li className="flex items-start gap-2 text-sm text-gray-300" data-id="element-5036">
                        <CheckCircle2Icon size={16} className="text-[#0EB4CC] shrink-0 mt-0.5" data-id="element-5037" />
                        Everything in Individual
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-300" data-id="element-5038">
                        <CheckCircle2Icon size={16} className="text-[#0EB4CC] shrink-0 mt-0.5" data-id="element-5039" />
                        Family coordination
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-300" data-id="element-5040">
                        <CheckCircle2Icon size={16} className="text-[#0EB4CC] shrink-0 mt-0.5" data-id="element-5041" />
                        Personalized paths per member
                      </li>
                      <li className="flex items-start gap-2 text-sm text-gray-300" data-id="element-5042">
                        <CheckCircle2Icon size={16} className="text-[#0EB4CC] shrink-0 mt-0.5" data-id="element-5043" />
                        Family Protection Score
                      </li>
                    </ul>
                    <button onClick={() => handlePlanSelect('family')} className="w-full py-3 rounded-xl font-bold text-[#1a0647] bg-white hover:bg-gray-100 transition-colors shadow-sm" data-id="element-5044">
                      Start Family Plan
                    </button>
                  </div>
                </div>

                {/* Add-on */}
                <div className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center justify-between shadow-sm mb-6" data-id="element-5045">
                  <div data-id="element-5046">
                    <h4 className="font-bold text-[#111111] text-sm" data-id="element-5047">
                      Remote Assist Add-on
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-5048">
                      Live implementation support
                    </p>
                    <p className="text-xs font-semibold text-[#8528e2] mt-1" data-id="element-5049">
                      +$9.99/month
                    </p>
                  </div>
                  <button onClick={() => setRemoteAssist(!remoteAssist)} className={`w-12 h-7 rounded-full transition-colors relative ${remoteAssist ? 'bg-[#8528e2]' : 'bg-gray-300'}`} data-id="element-5050">
                    <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${remoteAssist ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-5051" />
                  </button>
                </div>

                <p className="text-center text-xs text-gray-400 mt-auto" data-id="element-5052">
                  14-day free trial on paid plans. Cancel anytime.
                </p>
              </motion.div>}

            {step === 3 && <motion.div key="step3" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} className="absolute inset-0 w-full flex flex-col px-5 pb-10 overflow-y-auto hide-scrollbar" data-id="element-5053">
                <h2 className="text-2xl font-bold text-[#1a0647] mb-6" data-id="element-5054">
                  Checkout
                </h2>

                {/* Order Summary */}
                <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm mb-6" data-id="element-5055">
                  <div className="flex justify-between items-start mb-4" data-id="element-5056">
                    <div data-id="element-5057">
                      <h3 className="font-bold text-[#111111] capitalize" data-id="element-5058">
                        {selectedPlan} Plan
                      </h3>
                      <p className="text-sm text-gray-500 capitalize" data-id="element-5059">
                        {billingCycle} billing
                      </p>
                    </div>
                    <div className="text-right" data-id="element-5060">
                      <div className="font-bold text-[#111111]" data-id="element-5061">
                        $
                        {selectedPlan === 'individual' ? billingCycle === 'annual' ? '7.99' : '9.99' : billingCycle === 'annual' ? '19.99' : '24.99'}
                        <span className="text-sm font-normal text-gray-500" data-id="element-5062">
                          /mo
                        </span>
                      </div>
                    </div>
                  </div>
                  {remoteAssist && <div className="flex justify-between items-start mb-4 pt-4 border-t border-gray-100" data-id="element-5063">
                      <div data-id="element-5064">
                        <h3 className="font-medium text-[#111111] text-sm" data-id="element-5065">
                          Remote Assist
                        </h3>
                      </div>
                      <div className="text-right" data-id="element-5066">
                        <div className="font-medium text-[#111111] text-sm" data-id="element-5067">
                          $9.99<span className="text-gray-500" data-id="element-5068">/mo</span>
                        </div>
                      </div>
                    </div>}
                  <div className="bg-green-50 text-green-800 text-sm px-3 py-2 rounded-lg font-medium flex items-center gap-2" data-id="element-5069">
                    <CheckCircle2Icon size={16} className="text-green-600" data-id="element-5070" />
                    14-day free trial ends{' '}
                    {new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })}
                  </div>
                </div>

                {/* Express Pay */}
                <div className="space-y-3 mb-6" data-id="element-5071">
                  <button className="w-full bg-black text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-800 transition-colors" data-id="element-5072">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" data-id="element-5073">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.04 2.26-.74 3.58-.79 1.54-.04 2.74.69 3.48 1.84-3.02 1.76-2.51 5.62.44 6.85-.71 1.76-1.57 3.28-2.58 4.27zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" data-id="element-5074" />
                    </svg>
                    Pay
                  </button>
                  <button className="w-full bg-white border border-gray-300 text-[#111111] font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors shadow-sm" data-id="element-5075">
                    <svg className="w-10 h-5" viewBox="0 0 40 16" fill="none" data-id="element-5076">
                      <path d="M14.5 7.5C14.5 5.5 13 4 11 4C9 4 7.5 5.5 7.5 7.5C7.5 9.5 9 11 11 11C13 11 14.5 9.5 14.5 7.5ZM11 9.5C9.8 9.5 9 8.7 9 7.5C9 6.3 9.8 5.5 11 5.5C12.2 5.5 13 6.3 13 7.5C13 8.7 12.2 9.5 11 9.5ZM21.5 7.5C21.5 5.5 20 4 18 4C16 4 14.5 5.5 14.5 7.5C14.5 9.5 16 11 18 11C20 11 21.5 9.5 21.5 7.5ZM18 9.5C16.8 9.5 16 8.7 16 7.5C16 6.3 16.8 5.5 18 5.5C19.2 5.5 20 6.3 20 7.5C20 8.7 19.2 9.5 18 9.5ZM27.5 4.2V5.5H27.4C27 4.8 26 4 24.5 4C22.2 4 20.5 5.8 20.5 7.5C20.5 9.2 22.2 11 24.5 11C26 11 27 10.2 27.4 9.5H27.5V10.6C27.5 12 26.5 12.8 24.5 12.8C23 12.8 22 11.7 21.6 10.8L20.3 11.4C20.9 12.8 22.3 14.2 24.5 14.2C27.4 14.2 29 12.5 29 10.4V4.2H27.5ZM24.8 9.5C23.6 9.5 22.5 8.6 22.5 7.5C22.5 6.4 23.6 5.5 24.8 5.5C26 5.5 27 6.4 27 7.5C27 8.6 26 9.5 24.8 9.5ZM31.5 0.5V10.8H33V0.5H31.5ZM38.2 9.5C37.4 9.5 36.6 9.1 36.2 8.4L39.2 7.1L39 6.6C38.6 5.5 37.4 4 35.5 4C33.4 4 31.8 5.5 31.8 7.5C31.8 9.5 33.4 11 35.8 11C37.6 11 38.8 9.9 39.2 9.2L38 8.4C37.5 9.1 37 9.5 38.2 9.5ZM35.6 5.4C36.4 5.4 37 5.8 37.2 6.3L33.3 7.9C33.3 6.2 34.6 5.4 35.6 5.4ZM4.5 5.8L2.8 9.5H2.7L1 5.8H0L2.3 10.8L1.5 12.5H2.8L5.5 5.8H4.5Z" fill="#5F6368" data-id="element-5077" />
                      <path d="M6.8 4.2V11H8.3V4.2H6.8Z" fill="#5F6368" data-id="element-5078" />
                    </svg>
                  </button>
                </div>

                <div className="flex items-center gap-4 mb-6" data-id="element-5079">
                  <div className="flex-1 h-px bg-gray-200" data-id="element-5080"></div>
                  <span className="text-gray-400 text-sm font-medium" data-id="element-5081">
                    or pay with card
                  </span>
                  <div className="flex-1 h-px bg-gray-200" data-id="element-5082"></div>
                </div>

                {/* Card Form */}
                <div className="space-y-4 mb-6" data-id="element-5083">
                  <div data-id="element-5084">
                    <input type="text" placeholder="Card Number" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-[#111111] placeholder-gray-400 focus:outline-none focus:border-[#8528e2] focus:ring-1 focus:ring-[#8528e2] transition-all" data-id="element-5085" />
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-5086">
                    <input type="text" placeholder="MM/YY" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-[#111111] placeholder-gray-400 focus:outline-none focus:border-[#8528e2] focus:ring-1 focus:ring-[#8528e2] transition-all" data-id="element-5087" />
                    <input type="text" placeholder="CVV" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-[#111111] placeholder-gray-400 focus:outline-none focus:border-[#8528e2] focus:ring-1 focus:ring-[#8528e2] transition-all" data-id="element-5088" />
                  </div>
                  <div data-id="element-5089">
                    <input type="text" placeholder="Billing ZIP Code" className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3.5 text-[#111111] placeholder-gray-400 focus:outline-none focus:border-[#8528e2] focus:ring-1 focus:ring-[#8528e2] transition-all" data-id="element-5090" />
                  </div>
                </div>

                {/* Promo Code */}
                <details className="group mb-8" data-id="element-5091">
                  <summary className="flex items-center justify-between cursor-pointer list-none text-sm font-medium text-[#631bb7]" data-id="element-5092">
                    <span data-id="element-5093">Add promo code</span>
                    <ChevronDownIcon size={16} className="group-open:rotate-180 transition-transform" data-id="element-5094" />
                  </summary>
                  <div className="mt-3 flex gap-2" data-id="element-5095">
                    <input type="text" placeholder="Code" className="flex-1 bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#8528e2]" data-id="element-5096" />
                    <button className="bg-gray-100 text-[#111111] px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-200 transition-colors" data-id="element-5097">
                      Apply
                    </button>
                  </div>
                </details>

                <div className="flex items-center justify-between mb-6 pt-4 border-t border-gray-200" data-id="element-5098">
                  <span className="font-bold text-[#111111] text-lg" data-id="element-5099">
                    Total due today
                  </span>
                  <span className="font-bold text-[#111111] text-xl" data-id="element-5100">
                    $0.00
                  </span>
                </div>

                <Link to="/invite-family" className="block w-full bg-[#8528e2] text-white font-bold py-4 rounded-xl hover:bg-[#7322c4] transition-colors shadow-md mb-6 text-lg text-center no-underline" data-id="element-5101">
                  Start My Free Trial
                </Link>

                <div className="mt-auto flex items-center justify-center gap-2 text-gray-400 text-xs" data-id="element-5102">
                  <LockIcon size={12} data-id="element-5103" />
                  <span data-id="element-5104">
                    Secured by Stripe. Your payment info is never stored by
                    Caveo.
                  </span>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}